import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { toast } from 'sonner';
import { advisorOnboardingApi, advisorOnboardingKeys } from '@client/api/waterlily';
import { API } from '@client/api/types';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import {
  QUESTIONS,
  getNextUnansweredQuestion,
  getQuestionnaireProgress,
  getVisibleQuestions,
  isQuestionnaireComplete,
  type Question,
  type QuestionId,
  type QuestionnaireAnswers,
} from '@shared';

interface UseQuestionnaireOptions {
  advisorId: string;
}

export function useQuestionnaire({ advisorId }: UseQuestionnaireOptions) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: advisorOnboardingKeys.questionnaire(advisorId),
    queryFn: () => advisorOnboardingApi.getQuestionnaire(),
    staleTime: 60_000,
    enabled: !!advisorId,
  });

  const mutation = useMutation({
    mutationFn: (patch: Partial<QuestionnaireAnswers>) =>
      advisorOnboardingApi.putQuestionnaire(patch),
    onSuccess: result => {
      queryClient.setQueryData<API.AdvisorOnboardingQuestionnairePayload>(
        advisorOnboardingKeys.questionnaire(advisorId),
        prev =>
          prev
            ? {
                ...prev,
                responses: result.responses,
                inferredFields: result.inferredFields,
                isComplete: result.isComplete,
              }
            : prev,
      );
    },
    onError: error => {
      toast.error('Could not save your answer', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const payload = query.data;
  const responses: QuestionnaireAnswers = payload?.responses ?? {};
  const orgType = payload?.orgType ?? null;
  const organizationName = payload?.organizationName ?? null;
  const advisorFirstName = payload?.advisorFirstName ?? null;

  const isEdjPilotAdvisor = useIsEdjPilotAdvisor();

  const ctx = useMemo(
    () => ({
      answers: responses,
      orgType,
      organizationName,
      isEdjPilotAdvisor,
    }),
    [responses, orgType, organizationName, isEdjPilotAdvisor],
  );

  const visibleQuestions = useMemo(() => getVisibleQuestions(ctx), [ctx]);

  const nextUnanswered = useMemo(() => getNextUnansweredQuestion(ctx), [ctx]);

  const isComplete = useMemo(
    () => (payload?.isComplete ?? false) || isQuestionnaireComplete(ctx),
    [payload?.isComplete, ctx],
  );

  const getQuestionByPath = useCallback((questionPath: string | undefined): Question | null => {
    if (!questionPath) return null;

    const found = QUESTIONS.find(q => q.id === questionPath);

    return found ?? null;
  }, []);

  const isQuestionVisible = useCallback(
    (questionId: QuestionId) => visibleQuestions.some(q => q.id === questionId),
    [visibleQuestions],
  );

  const getProgress = useCallback(
    (questionId: QuestionId) => getQuestionnaireProgress(ctx, questionId),
    [ctx],
  );

  const submitAnswer = useCallback(
    async (patch: Partial<QuestionnaireAnswers>) => mutation.mutateAsync(patch),
    [mutation],
  );

  return {
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isSubmitting: mutation.isPending,
    error: query.error,
    payload,
    responses,
    orgType,
    organizationName,
    advisorFirstName,
    visibleQuestions,
    nextUnanswered,
    isComplete,
    isQuestionVisible,
    getQuestionByPath,
    getProgress,
    submitAnswer,
    refetch: query.refetch,
    isEdjPilotAdvisor,
  };
}
